import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { InputProps } from '../internal/input';
import Input from '../internal/input';
import ClearButton from './components/clear-button';
import SearchIcon from './components/search-icon';

type BaseProps = Omit<
  InputProps,
  | 'disabled'
  | 'hasError'
  | 'hint'
  | 'label'
  | 'mask'
  | 'placeholder'
  | 'type'
  | 'size'
  | 'prefixComponent'
  | 'suffixComponent'
>;

export type SearchInputProps = BaseProps & {
  clearButtonAriaLabel?: string;
  onReset?: () => void;
  placeholder?: string;
  size?: 'compact' | 'default';
};

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      clearButtonAriaLabel,
      onChange,
      onChangeText,
      onReset,
      placeholder,
      value,
      size = 'default',
      ...remainingProps
    }: SearchInputProps,
    ref,
  ) => {
    const { t } = useTranslation('ui');
    const handleClearInput = () => {
      onChangeText?.('');
      onReset?.();
    };

    // Always rendered, only hidden while empty, so the input reserves the clear button's padding
    // and keeps its width when the first character is typed.
    const renderSuffix = () => (
      <ClearButton
        // @ts-ignore: fails for dashboard project due to i18next selector
        aria-label={clearButtonAriaLabel ?? (t('components.search-input.clear-button-aria-label-default') as string)}
        isVisible={!!value}
        onClick={handleClearInput}
      />
    );

    return (
      <Input
        {...remainingProps}
        onChange={onChange}
        onChangeText={onChangeText}
        // @ts-ignore: fails for dashboard project due to i18next selector
        placeholder={placeholder ?? (t('components.search-input.placeholder-default') as string)}
        prefixComponent={<SearchIcon />}
        ref={ref}
        size={size}
        suffixComponent={renderSuffix()}
        value={value}
      />
    );
  },
);

export default SearchInput;
